var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import isPropValid from '@emotion/is-prop-valid';
import { Profile, Image as ImageIcon } from '@sumup/icons';
import styled from '../../styles/styled';
import { CircuitError } from '../../util/errors';
const avatarSizes = {
    yotta: '96px',
    giga: '48px',
};
const placeholders = {
    object: _jsx(ImageIcon, {}),
    identity: _jsx(Profile, {}),
};
const baseStyles = ({ theme, size }) => css `
  width: ${avatarSizes[size]};
  height: ${avatarSizes[size]};
  box-shadow: 0 0 0 ${theme.borderWidth.kilo} var(--cui-border-subtle);
  background-color: var(--cui-bg-subtle);
`;
const imageStyles = () => css `
  display: block;
  object-fit: cover;
  object-position: center;
`;
const borderRadiusStyles = ({ theme, variant, size, }) => {
    if (variant === 'identity') {
        return css `
      border-radius: ${theme.borderRadius.circle};
    `;
    }
    if (size === 'giga') {
        return css `
      border-radius: ${theme.borderRadius.byte};
    `;
    }
    return css `
    border-radius: ${theme.borderRadius.kilo};
  `;
};
const Image = styled('img', {
    shouldForwardProp: (prop) => isPropValid(prop),
})(baseStyles, borderRadiusStyles, imageStyles);
const placeholderStyles = ({ theme, size }) => css `
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--cui-fg-placeholder);
  /* Initials */
  font-size: calc(${avatarSizes[size]} / 2);
  font-weight: ${theme.fontWeight.bold};
  user-select: none;
`;
const Placeholder = styled('div', {
    shouldForwardProp: (prop) => isPropValid(prop),
})(baseStyles, borderRadiusStyles, placeholderStyles);
/**
 * The Avatar component displays an identity or an object image.
 */
export const Avatar = (_a) => {
    var { src, alt = '', // This default should be removed in the next major
    variant = 'object', size = 'yotta', initials } = _a, props = __rest(_a, ["src", "alt", "variant", "size", "initials"]);
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        variant === 'object' &&
        initials) {
        throw new CircuitError('Avatar', 'The `initials` prop can only be used with the identity `variant`. Remove the `initials` prop or change the `variant` to identity.');
    }
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        initials &&
        initials.length > 2) {
        throw new CircuitError('Avatar', `The \`initials\` prop is ${initials.length} characters long. Shorten it to 1-2 characters.`);
    }
    if (src) {
        return (_jsx(Image, Object.assign({ src: src, alt: alt, variant: variant, size: size }, props)));
    }
    const placeholder = variant === 'identity' && initials
        ? initials.slice(0, 2).toUpperCase()
        : placeholders[variant];
    return (_jsx(Placeholder, Object.assign({}, (alt
        ? { 'role': 'img', 'aria-label': alt }
        : { 'aria-hidden': 'true' }), { size: size, variant: variant }, props, { children: placeholder })));
};
